import { MaintenanceService } from '../../services/maintenance.service';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { TooltipDirective } from '../../../shared/components/ui/tooltip/tooltip.directive';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faPaperPlane } from '@fortawesome/pro-duotone-svg-icons/faPaperPlane';
import { faClock, faMessageLines } from '@fortawesome/pro-duotone-svg-icons';
import {
  faStar,
  faMicrophoneSlash,
  faVolumeSlash,
  faUserSlash,
  faBan,
} from '@fortawesome/pro-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/pro-regular-svg-icons';

import { UserAvatar } from '../user-avatar/user-avatar';
import { MatSliderWrapper } from '../../../shared/components/material-wrappers/mat-slider-wrapper/mat-slider-wrapper';
import { TextField } from '../../../shared/components/form-fields/text-field/text-field';
import { TextFieldAction } from '../../../shared/components/form-fields/text-field/text-field.types';
import { SquareIconBtn } from '../../../shared/components/ui/buttons/square-icon-btn/square-icon-btn';

import { User } from '../../../state/user.repository';
import { UserService } from '../../services/user.service';
import { OdinService } from '../../services/odin.service';
import { ModerationService } from '../../services/moderation.service';
import { ParticipantSettingsStore } from '../../../state/participant-settings.repository';
import { PermissionStore } from '../../../state/permissions.repository';
import { LanguageService } from '../../services/language.service';
import { formatDate } from '../../../shared/i18n/date-format';

const ONE_YEAR_SECONDS = 365 * 24 * 60 * 60;
const JOINED_DATE_FORMAT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
};

@Component({
  selector: 'andor-participant-mini-profile',
  standalone: true,
  imports: [
    AsyncPipe,
    ReactiveFormsModule,
    FaIconComponent,
    MatSlider,
    MatSliderThumb,
    TooltipDirective,
    TranslatePipe,
    UserAvatar,
    MatSliderWrapper,
    TextField,
    SquareIconBtn,
  ],
  templateUrl: './participant-mini-profile.html',
  styleUrl: './participant-mini-profile.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParticipantMiniProfile {
  readonly maintenance = inject(MaintenanceService);
  user = input.required<User>();

  readonly userService = inject(UserService);
  readonly odinService = inject(OdinService);
  readonly moderationService = inject(ModerationService);
  readonly participantSettingsStore = inject(ParticipantSettingsStore);
  readonly permissionsStore = inject(PermissionStore);
  private readonly _translateService = inject(TranslateService);
  private readonly _locale = inject(LanguageService).locale;

  readonly pokeControl = new FormControl(this._translateService.instant('Are you around?'), [
    Validators.maxLength(100),
  ]);

  readonly privateMessageControl = new FormControl('', [Validators.maxLength(1000)]);

  readonly inputMuted = computed(() => this.user().data.inputMuted);
  readonly outputMuted = computed(() => this.user().data.outputMuted);
  readonly status = computed(() => this.user().data.status);
  readonly displayName = computed(() => this.user().data.name);
  readonly statusMessage = computed(() => this.user().data.statusMessage ?? '');
  readonly role = computed(() => this.user().data.role ?? '');
  readonly statusTooltip = computed(() =>
    this.status() === 'online'
      ? this._translateService.instant('Online')
      : this._translateService.instant('Away')
  );
  readonly joinedAt = computed(() => this.user().data.joinedAt ?? null);

  private readonly _now = signal(Date.now());
  readonly joinedLabel = computed(() => {
    const joinedAt = this.joinedAt();
    if (joinedAt === null) return null;
    return this._formatJoinedRelative(joinedAt, this._now(), this._locale());
  });
  readonly joinedTooltip = computed(() => formatDate(this.joinedAt(), 'timestamp', this._locale()));

  readonly pokeAction: TextFieldAction = {
    icon: faPaperPlane,
    ariaLabel: this._translateService.instant('Send poke'),
    click: () => this.sendPoke(),
  };

  readonly privateMessageAction: TextFieldAction = {
    icon: faMessageLines,
    ariaLabel: this._translateService.instant('Send private message'),
    click: () => this.sendPrivateMessage(),
  };

  protected readonly faStar = faStar;
  protected readonly faStarRegular = faStarRegular;
  protected readonly faMicrophoneSlash = faMicrophoneSlash;
  protected readonly faVolumeSlash = faVolumeSlash;
  protected readonly faUserSlash = faUserSlash;
  protected readonly faBan = faBan;
  protected readonly faClock = faClock;

  constructor() {
    effect((onCleanup) => {
      if (this.joinedAt() === null) return;
      const id = setInterval(() => this._now.set(Date.now()), 30_000);
      onCleanup(() => clearInterval(id));
    });
  }

  private _formatJoinedRelative(joinedAt: number, now: number, locale: string): string {
    const diffMs = Math.max(0, now - joinedAt);
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) {
      return this._translateService.instant('Just joined');
    }
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) {
      return this._translateService.instant('Joined [count]m ago', { count: diffMin });
    }
    const diffHr = Math.floor(diffMin / 60);
    const remMin = diffMin % 60;
    if (diffHr < 24) {
      return remMin === 0
        ? this._translateService.instant('Joined [count]h ago', { count: diffHr })
        : this._translateService.instant('Joined [h]h [m]m ago', { h: diffHr, m: remMin });
    }
    return (
      this._translateService.instant('Joined') +
      ' ' +
      formatDate(joinedAt, JOINED_DATE_FORMAT, locale)
    );
  }

  async sendPoke() {
    const text = this.pokeControl.value;
    if (!text || text.length === 0) return;
    await this.odinService.sendMessage(
      {
        id: crypto.randomUUID(),
        scope: 'system',
        payload: text,
        kind: 'poke',
      },
      [this.user()]
    );
    this.pokeControl.reset(this._translateService.instant('Are you around?'));
  }

  async sendPrivateMessage() {
    const text = this.privateMessageControl.value?.trim();
    if (!text || this.privateMessageControl.invalid) return;
    // A private message is a normal text chat message scoped to a single
    // recipient. `OdinService.sendMessage` unicasts to the participant's
    // peerId and — because there is exactly one target — sets `receiver`,
    // which together with `scope: 'private'` is what marks it as a DM in
    // both the sender's and the receiver's chat timeline.
    await this.odinService.sendMessage(
      {
        id: crypto.randomUUID(),
        scope: 'private',
        kind: 'message',
        payload: { text },
      },
      [this.user()]
    );
    this.privateMessageControl.reset('');
  }

  async sendMute() {
    await this.odinService.sendMessage(
      {
        id: crypto.randomUUID(),
        scope: 'system',
        payload: '',
        kind: 'mute',
      },
      [this.user()]
    );
  }

  // Kick = no ban (the user may rejoin immediately). Pass the room we actually
  // joined as the (future) live-eviction target.
  async kickParticipant() {
    const roomId = this.odinService.currentRoomId ?? undefined;
    await this.moderationService.kickParticipant(this.user(), { roomId });
  }

  // Ban = kick with a one-year ban window. (Refactor later if finer control is
  // needed; one button per duration is fine for now.)
  async banParticipant() {
    const roomId = this.odinService.currentRoomId ?? undefined;
    await this.moderationService.kickParticipant(this.user(), {
      roomId,
      banDurationSeconds: ONE_YEAR_SECONDS,
    });
  }

  toggleFavorite() {
    this.userService.updateFavorite(this.user().id);
  }

  updateVolume(value: number) {
    this.userService.updateUserVolume(value, this.user());
  }

  isNameTruncated(el: HTMLElement): boolean {
    return el.scrollWidth > el.clientWidth;
  }
}
